import { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Stack, Typography } from '@mui/material';

import UserDisplay from '@/components/users/UserDisplay';
import { formatRedemptionAmount } from '@/helpers/redemption';
import type { CancelRedemptionReason, RedemptionSettlement } from '@/types';

import { DetailRow, StatusChip } from '../statusUi';
import { REDEMPTION_METHOD_REGISTRY } from './registry';

// Solid fills with white text so a card's status stands out against its grey background.
const BADGE = {
  green: '#2E7D32',
  amber: '#B45309',
  orange: '#C2410C',
  red: '#C62828',
  blue: '#1D4ED8',
};

export const SETTLEMENT_STATUS_META: Record<
  RedemptionSettlement['status'],
  { color: string; backgroundColor: string; label: string }
> = {
  issued: { color: 'common.white', backgroundColor: BADGE.amber, label: 'Issued' },
  claimed: { color: 'common.white', backgroundColor: BADGE.blue, label: 'Claimed' },
  fulfilled: { color: 'common.white', backgroundColor: BADGE.green, label: 'Fulfilled' },
  short: { color: 'common.white', backgroundColor: BADGE.orange, label: 'Short' },
  cancelled: { color: 'common.white', backgroundColor: BADGE.red, label: 'Cancelled' },
  unknown: { color: 'common.white', backgroundColor: BADGE.amber, label: 'Awaiting Response' },
};

//Record<NonNullable<RedemptionSettlement['outcome_class']>, string> =
// {
//   refused: 'Refused',
//   withdrawn: 'Withdrawn',
//   transient: 'Failed',
//   unknown: 'Unknown Outcome',
//}

/** Operator-facing labels for Unity's cancel reasons (stored on the settlement as outcome_code). */
export const CANCEL_REASON_LABELS: Record<CancelRedemptionReason, string> = {
  customer_changed_method: 'Customer changed their mind about the method',
  issued_in_error: 'Issued in error',
  other: 'Other',
};

export const getMethodLabel = (name: string) => {
  const definition = REDEMPTION_METHOD_REGISTRY[name];
  return definition ? `${definition.icon} ${definition.label}` : name;
};

const SettlementMethodData = ({ settlement }: { settlement: RedemptionSettlement }) => {
  const renderDetails = REDEMPTION_METHOD_REGISTRY[settlement.method]?.SettlementDetails;
  return renderDetails ? renderDetails(settlement) : null;
};

/**
 * Unity stores a withdrawal's reason as outcome_code and the operator's text as resolved_reason.
 * A preset reason renders as a chip; for "other" the operator's text is the reason itself.
 */
const CancellationReason = ({ settlement }: { settlement: RedemptionSettlement }) => {
  if (settlement.outcome_code === 'other') {
    return <>{settlement.resolved_reason || CANCEL_REASON_LABELS.other}</>;
  }

  const code = settlement.outcome_code as CancelRedemptionReason;
  return (
    <StatusChip
      label={CANCEL_REASON_LABELS[code] ?? settlement.outcome_code}
      data-test={`redemption-cancel-reason-chip-${settlement.id}`}
    />
  );
};

type SettlementCardProps = {
  settlement: RedemptionSettlement;
  /** Header toggles the details (used for History); otherwise details are always shown. */
  collapsible?: boolean;
  defaultOpen?: boolean;
};

export const SettlementCard = ({
  settlement,
  collapsible = false,
  defaultOpen = true,
}: SettlementCardProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = SETTLEMENT_STATUS_META[settlement.status];
  const isCancelled = settlement.status === 'cancelled';
  const isWithdrawn = isCancelled && settlement.outcome_class === 'withdrawn';
  const expanded = !collapsible || open;

  const details = (
    <>
      <DetailRow
        label="Amount"
        value={formatRedemptionAmount(
          settlement.method,
          settlement.asset_amount,
          settlement.fiat_amount,
        )}
      />
      {settlement.status === 'short' && settlement.delivered_asset_amount != null && (
        <DetailRow
          label="Delivered"
          value={`${settlement.delivered_asset_amount} of ${settlement.asset_amount} GB`}
        />
      )}
      {settlement.reference && <DetailRow label="Reference" value={settlement.reference} wrap />}
      <DetailRow
        label="Issued By"
        value={<UserDisplay userId={settlement.created_by} fallback="N/A" />}
      />
      <DetailRow label="Issued At" value={new Date(settlement.created_at).toLocaleString()} />
      <SettlementMethodData settlement={settlement} />
      {isWithdrawn && settlement.outcome_code && (
        <DetailRow
          label="Cancellation Reason"
          value={<CancellationReason settlement={settlement} />}
          wrap
        />
      )}
      {/* For a preset reason, the operator's optional text sits beside the chip. */}
      {isWithdrawn && settlement.outcome_code !== 'other' && settlement.resolved_reason && (
        <DetailRow label="Cancellation Notes" value={settlement.resolved_reason} wrap />
      )}
      {isCancelled && !isWithdrawn && settlement.outcome_code && (
        <DetailRow label="Switch" value={settlement.outcome_code} />
      )}
      {isCancelled && settlement.resolved_by && (
        <DetailRow
          label="Resolved By"
          value={
            <>
              <UserDisplay userId={settlement.resolved_by} />
              {settlement.resolved_at && ` · ${new Date(settlement.resolved_at).toLocaleString()}`}
            </>
          }
        />
      )}
      {settlement.notes && <DetailRow label="Notes" value={settlement.notes} />}
    </>
  );

  return (
    <Box
      data-test={`redemption-settlement-${settlement.id}`}
      sx={{
        backgroundColor: 'neutral.100',
        borderRadius: '12px',
        px: 2,
        pb: expanded ? 0 : 1.5,
        '& > div:last-child, & .MuiCollapse-wrapperInner > div:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        onClick={collapsible ? () => setOpen((current) => !current) : undefined}
        sx={{ pt: 1.5, cursor: collapsible ? 'pointer' : 'default' }}
        data-test={collapsible ? `redemption-settlement-toggle-${settlement.id}` : undefined}
      >
        <Typography variant="body2" fontWeight={600}>
          {getMethodLabel(settlement.method)} · attempt #{settlement.attempt_no}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <StatusChip
            fill={meta.backgroundColor}
            label={isCancelled && settlement.status === 'cancelled' ? 'Cancelled' : meta.label}
            data-test={`redemption-settlement-status-${settlement.id}`}
          />
          {collapsible && (
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}
            />
          )}
        </Stack>
      </Stack>
      {collapsible ? <Collapse in={open}>{details}</Collapse> : details}
    </Box>
  );
};
